import {CancelTokenSource} from "axios";
import {RouteMap} from "./applicationReducer";
import {ConfigState, LeadFormState} from "../contract/setting";
import { Theme } from '@emotion/react';

export type ApplicationAction =
  SetAuthenticatedUserAction
  | SetConfigAction
  | SetThemeAction
  | SetRoutesAction
  | SetLoadingAction
  | SetOfflineAction
  | SetArticlesIndexAction
  | SetArticlesVisibleAction
  | SetArticlesReadyAction
  | SetArticlesLoadingAction
  | SetArticlesFailedAction
  | SetArticlesCountAction
  | SetArticlesUnavailableAction
  | PushCancelToken
  | PushAbortController
  | ClearCancelTokens
  | ClearAbortControllers;

interface SetAuthenticatedUserAction {
  type: 'application/setAuthenticatedUser';
  authenticated_user_id: number;
}

interface SetConfigAction {
  type: 'application/setConfig';
  config: ConfigState;
  lead_form: LeadFormState;
}

interface SetThemeAction {
  type: 'application/setTheme';
  theme: Theme;
}

interface SetRoutesAction {
  type: 'application/setRoutes';
  routes: RouteMap;
}

interface SetLoadingAction {
  type: 'application/setLoading';
  loading: string[];
}

interface SetOfflineAction {
  type: 'application/setOffline';
  offline: boolean;
}

interface SetArticlesIndexAction {
  type: 'application/setArticlesIndex';
  route: string;
  articles: string[];
}

interface SetArticlesVisibleAction {
  type: 'application/setArticlesVisible';
  route: string;
  articles: string[];
}

interface SetArticlesReadyAction {
  type: 'application/setArticlesReady';
  route: string;
  articles: string[];
}

interface SetArticlesLoadingAction {
  type: 'application/setArticlesLoading';
  route: string;
  chunk: string;
  articles: string[];
}

interface SetArticlesFailedAction {
  type: 'application/setArticlesFailed';
  route: string;
  articles: string[];
}

interface SetArticlesCountAction {
  type: 'application/setArticlesCount';
  route: string;
  count: number;
}

interface SetArticlesUnavailableAction {
  type: 'application/setArticlesUnavailable';
  route: string;
}

interface PushCancelToken {
  type: 'application/pushCancelToken';
  cancel_token: CancelTokenSource;
}

interface PushAbortController {
  type: 'application/pushAbortController';
  abort_controller: AbortController;
}

interface ClearCancelTokens {
  type: 'application/clearCancelTokens';
}

interface ClearAbortControllers {
  type: 'application/clearAbortControllers';
}

export function setAuthenticatedUser(id: number): SetAuthenticatedUserAction {
  return {
    type: 'application/setAuthenticatedUser',
    authenticated_user_id: id
  };
}

export function setConfig(config: ConfigState, lead_form: LeadFormState): SetConfigAction {
  return {
    type: 'application/setConfig',
    config,
    lead_form
  }
}

export function setTheme(theme: Theme): SetThemeAction {
  return {
    type: 'application/setTheme',
    theme
  }
}

export function setRoutes(routes: RouteMap): SetRoutesAction {
  return {
    type: 'application/setRoutes',
    routes,
  }
}

export function setLoading(loading: string[]): SetLoadingAction {
  return {
    type: 'application/setLoading',
    loading,
  }
}

export function setOffline(offline: boolean): SetOfflineAction {
  return {
    type: 'application/setOffline',
    offline,
  }
}

export function setArticlesIndex(route: string, articles: string[]): SetArticlesIndexAction {
  return {
    type: 'application/setArticlesIndex',
    route,
    articles,
  }
}

export function setArticlesVisible(route: string, articles: string[]): SetArticlesVisibleAction {
  return {
    type: 'application/setArticlesVisible',
    route,
    articles,
  }
}

export function setArticlesReady(route: string, articles: string[]): SetArticlesReadyAction {
  return {
    type: 'application/setArticlesReady',
    route,
    articles,
  }
}

export function setArticlesLoading(route: string, chunk: string, articles: string[]): SetArticlesLoadingAction {
  return {
    type: 'application/setArticlesLoading',
    route,
    chunk,
    articles,
  }
}

export function setArticlesFailed(route: string, articles: string[]): SetArticlesFailedAction {
  return {
    type: 'application/setArticlesFailed',
    route,
    articles,
  }
}

export function setArticlesCount(route: string, count: number): SetArticlesCountAction {
  return {
    type: 'application/setArticlesCount',
    route,
    count,
  }
}

export function setArticlesUnavailable(route: string): SetArticlesUnavailableAction {
  return {
    type: 'application/setArticlesUnavailable',
    route,
  }
}

export function pushCancelToken(token: CancelTokenSource): PushCancelToken {
  return {
    type: 'application/pushCancelToken',
    cancel_token: token
  }
}

export function pushAbortController(abortController: AbortController): PushAbortController {
  return {
    type: 'application/pushAbortController',
    abort_controller: abortController
  }
}

export function clearCancelTokens(): ClearCancelTokens {
  return {
    type: 'application/clearCancelTokens'
  }
}

export function clearAbortControllers(): ClearAbortControllers {
  return {
    type: 'application/clearAbortControllers'
  }
}
